/**
 * ============================================================================
 * Iron Meridian - Tutorial Overlay Component
 * ============================================================================
 * 
 * Lightweight, dismissible tutorial system for first-time players.
 * Shows step-by-step guidance without modal spam.
 * Persists completion status in localStorage.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

// ============================================================================
// TUTORIAL CONFIGURATION
// ============================================================================

const TUTORIAL_STORAGE_KEY = 'ironmeridian.tutorialComplete.v1'

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Iron Meridian',
    content: 'Your goal is global dominance. Build your economy, grow your military, and conquer the world through strategic decision-making.',
    target: null, // No highlight, center overlay
    position: 'center',
    icon: '🌍'
  },
  {
    id: 'map',
    title: 'The World Map',
    content: 'This is your territory. Click any country to view its details and available actions.',
    target: '[data-tutorial="player-country"]',
    fallbackTarget: '.country-path.player-owned',
    position: 'right',
    icon: '🗺️',
    highlight: true
  },
  {
    id: 'country-panel',
    title: 'Country Details',
    content: 'Here you can see the selected country\'s stats: military power, economy, technology, and stability.',
    target: '[data-tutorial="country-panel"]',
    fallbackTarget: 'aside:first-of-type',
    position: 'right',
    icon: '📊'
  },
  {
    id: 'actions',
    title: 'Queue Actions',
    content: 'Plan your moves here. Attack neighbors, build infrastructure, or research technology before ending your turn.',
    target: '[data-tutorial="action-panel"]',
    fallbackTarget: 'aside:last-of-type',
    position: 'left',
    icon: '⚔️'
  },
  {
    id: 'turn',
    title: 'End Your Turn',
    content: 'When ready, end your turn to resolve all actions simultaneously. Watch the results unfold!',
    target: '[data-tutorial="turn-control"]',
    fallbackTarget: '[data-tutorial="action-panel"]',
    position: 'left',
    icon: '⏭️'
  }
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function isTutorialComplete() {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(TUTORIAL_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function markTutorialComplete() {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true')
  } catch {
    // Ignore storage errors
  }
}

export function resetTutorial() {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(TUTORIAL_STORAGE_KEY)
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// SPOTLIGHT HIGHLIGHT COMPONENT
// ============================================================================

function SpotlightOverlay({ targetRect, isVisible }) {
  if (!isVisible || !targetRect) return null

  // Create a spotlight effect using CSS clip-path
  const padding = 8
  const x = targetRect.left - padding
  const y = targetRect.top - padding
  const width = targetRect.width + padding * 2
  const height = targetRect.height + padding * 2

  return createPortal(
    <div 
      className="fixed inset-0 z-40 pointer-events-none animate-fade-in"
      style={{
        background: `radial-gradient(ellipse ${width + 100}px ${height + 100}px at ${x + width/2}px ${y + height/2}px, transparent 30%, rgba(0, 0, 0, 0.7) 100%)`
      }}
    />,
    document.body
  )
}

// ============================================================================
// TUTORIAL TOOLTIP COMPONENT
// ============================================================================

function TutorialTooltip({ 
  step, 
  stepIndex, 
  totalSteps, 
  targetRect, 
  onNext, 
  onSkip,
  onBack 
}) {
  const tooltipRef = useRef(null)
  const [position, setPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' })

  useEffect(() => {
    if (!tooltipRef.current) return

    if (step.position === 'center' || !targetRect) {
      // Center in viewport
      setPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      })
      return
    }

    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const padding = 20
    let top, left, transform = 'none'

    switch (step.position) {
      case 'right':
        top = targetRect.top + targetRect.height / 2
        left = targetRect.right + padding
        transform = 'translateY(-50%)'
        // Check right edge
        if (left + tooltipRect.width > window.innerWidth - padding) {
          left = targetRect.left - tooltipRect.width - padding
        }
        break
      case 'left':
        top = targetRect.top + targetRect.height / 2
        left = targetRect.left - tooltipRect.width - padding
        transform = 'translateY(-50%)'
        // Check left edge
        if (left < padding) {
          left = targetRect.right + padding
        }
        break
      case 'bottom':
        top = targetRect.bottom + padding
        left = targetRect.left + targetRect.width / 2
        transform = 'translateX(-50%)'
        break
      case 'top':
        top = targetRect.top - tooltipRect.height - padding
        left = targetRect.left + targetRect.width / 2
        transform = 'translateX(-50%)'
        break
      default:
        top = '50%'
        left = '50%'
        transform = 'translate(-50%, -50%)'
    }

    // Clamp to viewport
    top = Math.max(padding, Math.min(window.innerHeight - tooltipRect.height - padding, top))
    left = Math.max(padding, Math.min(window.innerWidth - tooltipRect.width - padding, left))

    setPosition({ top: `${top}px`, left: `${left}px`, transform })
  }, [step, targetRect])

  const isFirst = stepIndex === 0
  const isLast = stepIndex === totalSteps - 1

  return createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-50 w-80 bg-war-room-800/95 backdrop-blur-md border border-war-room-600/50 rounded-2xl shadow-2xl animate-tutorial-in"
      style={position}
      role="dialog"
      aria-labelledby="tutorial-title"
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 pb-2">
        <span className="text-2xl">{step.icon}</span>
        <h3 id="tutorial-title" className="text-lg font-semibold text-white">{step.title}</h3>
      </div>

      {/* Content */}
      <div className="px-4 pb-4">
        <p className="text-sm text-gray-300 leading-relaxed">{step.content}</p>
      </div>

      {/* Progress & Actions */}
      <div className="flex items-center justify-between p-4 pt-2 border-t border-war-room-600/50">
        {/* Progress dots */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === stepIndex 
                  ? 'bg-war-room-accent w-4' 
                  : idx < stepIndex 
                    ? 'bg-green-500' 
                    : 'bg-war-room-600'
              }`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              onClick={onBack}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-war-room-accent rounded"
            >
              Back
            </button>
          )}
          <button
            onClick={onSkip}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-war-room-accent rounded"
          >
            Skip
          </button>
          <button
            onClick={onNext}
            className="px-4 py-1.5 text-sm bg-war-room-accent hover:bg-blue-600 rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-war-room-accent focus:ring-offset-2 focus:ring-offset-war-room-800"
          >
            {isLast ? 'Start Playing' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// MAIN TUTORIAL OVERLAY COMPONENT
// ============================================================================

export default function TutorialOverlay({ 
  isActive = true, 
  onComplete,
  forceShow = false 
}) {
  const [currentStep, setCurrentStep] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [targetRect, setTargetRect] = useState(null)

  // Check if tutorial should show
  useEffect(() => {
    if (forceShow) {
      setIsVisible(true)
      setCurrentStep(0)
    } else if (isActive && !isTutorialComplete()) {
      // Delay tutorial start slightly to let the game render
      const timeout = setTimeout(() => {
        setIsVisible(true)
      }, 500)
      return () => clearTimeout(timeout)
    }
  }, [isActive, forceShow])

  // Find and track target element
  useEffect(() => {
    if (!isVisible) return

    const step = TUTORIAL_STEPS[currentStep]
    if (!step.target && !step.fallbackTarget) {
      setTargetRect(null)
      return
    }

    const findTarget = () => {
      let element = step.target ? document.querySelector(step.target) : null
      if (!element && step.fallbackTarget) {
        element = document.querySelector(step.fallbackTarget)
      }
      if (element) {
        setTargetRect(element.getBoundingClientRect())
      } else {
        setTargetRect(null)
      }
    }

    findTarget()
    
    // Update on resize
    window.addEventListener('resize', findTarget)
    
    // Also update periodically in case elements move
    const interval = setInterval(findTarget, 500)

    return () => {
      window.removeEventListener('resize', findTarget)
      clearInterval(interval)
    }
  }, [isVisible, currentStep])

  // Keyboard handling
  useEffect(() => {
    if (!isVisible) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleSkip()
      } else if (e.key === 'Enter' || e.key === 'ArrowRight') {
        handleNext()
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        handleBack()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isVisible, currentStep])

  const handleNext = useCallback(() => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      // Tutorial complete
      markTutorialComplete()
      setIsVisible(false)
      onComplete?.()
    }
  }, [currentStep, onComplete])

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }, [currentStep])

  const handleSkip = useCallback(() => {
    markTutorialComplete()
    setIsVisible(false)
    onComplete?.()
  }, [onComplete])

  if (!isVisible) return null

  const step = TUTORIAL_STEPS[currentStep]

  return (
    <>
      <SpotlightOverlay 
        targetRect={step.highlight ? targetRect : null} 
        isVisible={isVisible} 
      />
      <TutorialTooltip
        step={step}
        stepIndex={currentStep}
        totalSteps={TUTORIAL_STEPS.length}
        targetRect={targetRect}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={handleSkip}
      />
    </>
  )
}
